import { Button, PreloaderCircular, RadioButton } from '@shared/ui/kit';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles.ts';
import ExcelIcon from './ui/excel-icon.tsx';
import ButtonWithPlus from '@shared/ui/button-with-plus/index.tsx';
import useUploadFileDnd from '@shared/hooks/useUploadFileDnd.ts';
import { useRef, useState } from 'react';
import { ModalSegment } from '@features/modal-segment/index.tsx';
import { useToast } from '@shared/hooks/useToast.ts';
import { uploadGuestFile, uploadStructureFile } from '@pages/event/api/excelUpload.ts';
import { downloadTemplate } from '@pages/event/api/excelTemplate.ts';
import { ErrorIcon } from '@shared/assets/icons/error.tsx';
import { GuestsData } from '@pages/event/api/useAddParticipantsToCategory';
import type { SminexClubInvalidClientItem } from '@features/clients/api/sminexClubExcel';

interface WithExcelProps {
  onSuccessUpload: (data: number[] | GuestsData[]) => void;
  setShowModal: (value: boolean) => void;
}

export interface UploadedStructureData {
  totalUsers: number;
  succsessUsers: number;
  errorUsers: number;
  errorUsersData: string[];
  successUsersData: number[];
  invalid_clients?: SminexClubInvalidClientItem[];
}

export interface UploadedGuestData {
  totalGuests: number;
  errorGuests: number;
  errorGuestsData: {
    lastName: string;
    firstName: string;
    email: string;
    phone: string;
    errorText: string;
  }[];
  successGuest: number;
  successGuestData: {
    lastName: string;
    firstName: string;
    email: string;
    phone: string;
  }[];
}

type UploadData = UploadedStructureData | UploadedGuestData;
type ImportTarget = 'structure' | 'freelance';

const ExcelUploadStatus: React.FC<{ isLoading: boolean; hasError: boolean; errorFile: boolean }> = ({
  isLoading,
  hasError,
  errorFile,
}) => {
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <PreloaderCircular />
      </div>
    );
  }

  if (hasError) {
    return <div {...stylex.props(styles.textError)}>Неверный формат файла. Ожидается файл формата .xlsx.</div>;
  }

  if (errorFile) {
    return (
      <div {...stylex.props(styles.textError)}>
        <ErrorIcon /> При загрузке файла возникла непредвиденная ошибка. Попробуйте загрузить
        <br /> файл повторно
      </div>
    );
  }

  return null;
};

interface ExcelImportFormProps {
  importTarget: ImportTarget;
  excelInputRef: React.RefObject<HTMLInputElement>;
  onImportTargetChange: (value: ImportTarget) => void;
  onDownloadTemplate: () => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onImportClick: (event: React.MouseEvent<Element, MouseEvent>) => void;
}

const ExcelImportForm: React.FC<ExcelImportFormProps> = ({
  importTarget,
  excelInputRef,
  onImportTargetChange,
  onDownloadTemplate,
  onDrop,
  onDragLeave,
  onDragOver,
  onFileChange,
  onImportClick,
}) => (
  <>
    <div {...stylex.props(styles.steps)}>
      <p {...stylex.props(styles.stepsText, styles.firstStepText)}>Шаг 1 *</p>
      <p {...stylex.props(styles.stepsDescription)}>Выберете куда импортировать участников</p>
      <div {...stylex.props(styles.importTargetGroup)}>
        <div {...stylex.props(styles.importTargetOption)}>
          <div {...stylex.props(styles.importTargetRadio)}>
            <RadioButton
              isChecked={importTarget === 'structure'}
              changeFunction={() => onImportTargetChange('structure')}
              size="medium"
            />
          </div>
          <span {...stylex.props(styles.importTargetLabel)}>Орг. структура</span>
        </div>
        <div {...stylex.props(styles.importTargetOption)}>
          <div {...stylex.props(styles.importTargetRadio)}>
            <RadioButton
              isChecked={importTarget === 'freelance'}
              changeFunction={() => onImportTargetChange('freelance')}
              size="medium"
            />
          </div>
          <span {...stylex.props(styles.importTargetLabel)}>Внештатные участники</span>
        </div>
      </div>
      <div {...stylex.props(styles.stepsTwo)}>
        <p {...stylex.props(styles.stepsText)}>Шаг 2 *</p>
      </div>
      <Button variant="secondary" size="small" settings={{ padding: '8px 20px' }} onClick={onDownloadTemplate}>
        <span {...stylex.props(styles.textBtn)}>
          {importTarget === 'structure' ? 'Скачать шаблон.xlsx' : 'Скачать шаблон внештатных участников.xlsx'}
        </span>
      </Button>
    </div>
    <div {...stylex.props(styles.steps, styles.stepThree)}>
      <p {...stylex.props(styles.stepsText)}>Шаг 3 *</p>
      <div onDragLeave={onDragLeave} onDragOver={onDragOver} onDrop={onDrop} {...stylex.props(styles.excelInput())}>
        <ExcelIcon />
        <div {...stylex.props(styles.excelText)}>Импортируйте файл Excel с предварительно заполненными участниками</div>
        <input type="file" ref={excelInputRef} style={{ display: 'none' }} onChange={onFileChange} />
        <ButtonWithPlus labelText="Импортировать" handleClick={onImportClick} />
      </div>
    </div>
  </>
);

const WithExcel: React.FC<WithExcelProps> = ({ onSuccessUpload }) => {
  const [uploadedData, setUploadedData] = useState<UploadData | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorFile, setErrorFile] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [importTarget, setImportTarget] = useState<ImportTarget>('structure');

  const { showToast } = useToast();

  const { handleDragLeave, handleDragOver, setDragActive } = useUploadFileDnd();

  const excelInputRef = useRef<HTMLInputElement>(null);

  const handleClick = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault();
    excelInputRef?.current?.click();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    const file = event.dataTransfer.files?.length && event.dataTransfer.files[0];

    if (file) {
      const { size } = file;
      const maxSize = 1.5 * 1024 * 1024;
      if (size > maxSize) return;
      if (excelInputRef.current) {
        excelInputRef.current.files = event.dataTransfer.files;
      }

      handleUploadFile(file);
    }
  };
  const resetToInitialState = () => {
    setUploadedData(null);
    setIsLoading(false);
    setHasError(false);
    setErrorFile(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      const file = event.target.files[0];
      handleUploadFile(file);
    }
  };

  const handleUploadFile = async (file: File) => {
    setIsLoading(true);
    setHasError(false);
    setErrorFile(false);

    const validExtensions = ['xlsx'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (!validExtensions.includes(fileExtension || '')) {
      setHasError(true);
      showToast('Ошибка при добавлении сотрудников', 'error');
      setIsLoading(false);
      return;
    }

    try {
      if (importTarget === 'structure') {
        const responseData = await uploadStructureFile(file);
        setUploadedData(responseData);
        setShowModal(true);
        onSuccessUpload(responseData?.successUsersData);
      } else {
        const responseData = await uploadGuestFile(file);
        setUploadedData(responseData);
        setShowModal(true);
        onSuccessUpload(responseData.successGuestData);
      }
    } catch (error) {
      console.error('Ошибка при загрузке файла:', error);
      setErrorFile(true);
    }

    setIsLoading(false);
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadTemplate(importTarget === 'freelance' ? 'guest' : 'structure');
    } catch (error) {
      console.error('Ошибка при скачивании шаблона:', error);
    }
  };

  const showStatus = isLoading || hasError || errorFile;
  const showResult = Boolean(uploadedData && showModal);

  return (
    <div {...stylex.props(styles.withExcelBase)}>
      {showStatus && <ExcelUploadStatus isLoading={isLoading} hasError={hasError} errorFile={errorFile} />}
      {!showStatus && showResult && uploadedData && (
        <ModalSegment uploadedData={uploadedData} onBack={resetToInitialState} setShowModal={setShowModal} />
      )}
      {!showStatus && !showResult && (
        <ExcelImportForm
          importTarget={importTarget}
          excelInputRef={excelInputRef}
          onImportTargetChange={setImportTarget}
          onDownloadTemplate={handleDownloadTemplate}
          onDrop={handleDrop}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onFileChange={handleFileChange}
          onImportClick={handleClick}
        />
      )}
    </div>
  );
};

export default WithExcel;
